import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Edit3, Save, Tag, Layers, FileText, Info, ChevronDown, ChevronRight, StickyNote, BarChart3, Lightbulb } from 'lucide-react';
import { generateAPAInterpretation } from './qualitativeUtils';

const GroundedTheory = ({ texts, surveyId }) => {
  const [codes, setCodes] = useState(() => {
    const saved = localStorage.getItem(`gt-codes-${surveyId}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem(`gt-categories-${surveyId}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [memos, setMemos] = useState(() => {
    const saved = localStorage.getItem(`gt-memos-${surveyId}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [codedResponses, setCodedResponses] = useState(() => {
    const saved = localStorage.getItem(`gt-coded-${surveyId}`);
    return saved ? JSON.parse(saved) : {};
  });

  const [newCode, setNewCode] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newMemo, setNewMemo] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [view, setView] = useState('coding');

  const saveCodes = (c) => { setCodes(c); localStorage.setItem(`gt-codes-${surveyId}`, JSON.stringify(c)); };
  const saveCategories = (c) => { setCategories(c); localStorage.setItem(`gt-categories-${surveyId}`, JSON.stringify(c)); };
  const saveMemos = (m) => { setMemos(m); localStorage.setItem(`gt-memos-${surveyId}`, JSON.stringify(m)); };
  const saveCodedResponses = (c) => { setCodedResponses(c); localStorage.setItem(`gt-coded-${surveyId}`, JSON.stringify(c)); };

  const addCode = () => {
    if (newCode.trim()) {
      const code = { id: Date.now().toString(), name: newCode.trim(), categoryId: null };
      saveCodes([...codes, code]);
      setNewCode('');
    }
  };

  const removeCode = (id) => {
    saveCodes(codes.filter(c => c.id !== id));
    const updated = { ...codedResponses };
    Object.keys(updated).forEach(respId => {
      updated[respId] = updated[respId].filter(cId => cId !== id);
    });
    saveCodedResponses(updated);
  };

  const addCategory = () => {
    if (newCategory.trim()) {
      const cat = { id: Date.now().toString(), name: newCategory.trim() };
      saveCategories([...categories, cat]);
      setNewCategory('');
    }
  };

  const removeCategory = (id) => {
    saveCategories(categories.filter(c => c.id !== id));
    saveCodes(codes.map(c => c.categoryId === id ? { ...c, categoryId: null } : c));
  };

  const assignCodeToCategory = (codeId, categoryId) => {
    saveCodes(codes.map(c => c.id === codeId ? { ...c, categoryId } : c));
  };

  const toggleCodeOnResponse = (responseIdx, codeId) => {
    const current = codedResponses[responseIdx] || [];
    const updated = current.includes(codeId)
      ? current.filter(c => c !== codeId)
      : [...current, codeId];
    saveCodedResponses({ ...codedResponses, [responseIdx]: updated });
  };

  const addMemo = () => {
    if (newMemo.trim()) {
      const memo = { 
        id: Date.now().toString(), 
        text: newMemo.trim(), 
        date: new Date().toISOString(),
        codeId: null
      };
      saveMemos([...memos, memo]);
      setNewMemo('');
    }
  };

  const removeMemo = (id) => {
    saveMemos(memos.filter(m => m.id !== id));
  };

  const analysis = useMemo(() => {
    const codeFrequency = {};
    codes.forEach(c => { codeFrequency[c.id] = 0; });
    
    Object.values(codedResponses).forEach(codeList => {
      codeList.forEach(cId => {
        if (codeFrequency[cId] !== undefined) {
          codeFrequency[cId]++;
        }
      });
    });

    const codedCount = Object.keys(codedResponses).filter(k => codedResponses[k]?.length > 0).length;
    const saturationScore = texts.length > 0 ? Math.round((codedCount / texts.length) * 100) : 0;
    
    let saturationLevel = 'low';
    if (saturationScore > 80) saturationLevel = 'high';
    else if (saturationScore > 50) saturationLevel = 'moderate';

    return {
      codeCount: codes.length,
      categoryCount: categories.length,
      codedResponses: codedCount,
      totalResponses: texts.length,
      saturationScore,
      saturationLevel,
      codeFrequency,
      memoCount: memos.length
    };
  }, [codes, categories, codedResponses, texts, memos]);

  const interpretation = useMemo(() => 
    generateAPAInterpretation('grounded', analysis),
    [analysis]
  );

  const uncategorizedCodes = codes.filter(c => !c.categoryId);
  const categorizedCodes = categories.map(cat => ({
    ...cat,
    codes: codes.filter(c => c.categoryId === cat.id)
  }));

  const groundedSummary = useMemo(() => {
    const categorizedCount = codes.filter(c => c.categoryId).length;
    const uncategorizedCount = codes.length - categorizedCount;
    
    return `This grounded theory analysis engaged in systematic coding of ${analysis.totalResponses} text segments, developing ${analysis.codeCount} open codes through line-by-line analysis. These codes have been organized into ${analysis.categoryCount} axial categories, with ${categorizedCount} codes assigned to categories and ${uncategorizedCount} remaining uncategorized. The current theoretical saturation stands at ${analysis.saturationScore}% (${analysis.codedResponses} of ${analysis.totalResponses} responses coded), indicating ${analysis.saturationLevel === 'high' ? 'strong progress toward theoretical saturation where new data no longer generates new codes' : analysis.saturationLevel === 'moderate' ? 'moderate coverage requiring continued coding and comparison' : 'early-stage analysis requiring substantial additional coding'}. ${analysis.memoCount} theoretical memos have been written to capture emerging insights and relationships. ${analysis.categoryCount > 0 ? `The axial coding process reveals conceptual relationships between open codes, suggesting emerging theoretical patterns.` : 'Creating axial categories will help organize codes into higher-order concepts.'} Researchers should continue constant comparison between data and emerging theory, write theoretical memos regularly, and consider how categories relate to form a coherent theoretical framework. Triangulation with other qualitative methods can strengthen theoretical development.`;
  }, [analysis, codes]);

  return (
    <div className="grounded-theory">
      <div className="analysis-header">
        <h4><Layers size={20} /> Grounded Theory Coding</h4>
        <p>Build theory from data through open coding, axial categorization, and memo writing</p>
      </div>

      <div className="analysis-guidance">
        <Lightbulb size={18} />
        <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
      </div>

      <div className="gt-tabs">
        <button 
          className={view === 'coding' ? 'active' : ''} 
          onClick={() => setView('coding')}
        >
          <Tag size={16} /> Open Coding
        </button>
        <button 
          className={view === 'axial' ? 'active' : ''} 
          onClick={() => setView('axial')}
        >
          <Layers size={16} /> Axial Coding
        </button>
        <button 
          className={view === 'memos' ? 'active' : ''} 
          onClick={() => setView('memos')}
        >
          <StickyNote size={16} /> Memos
        </button>
        <button 
          className={view === 'summary' ? 'active' : ''} 
          onClick={() => setView('summary')}
        >
          <BarChart3 size={16} /> Summary
        </button>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-value">{analysis.codeCount}</div>
          <div className="stat-label">Open Codes</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.categoryCount}</div>
          <div className="stat-label">Axial Categories</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.codedResponses}/{analysis.totalResponses}</div>
          <div className="stat-label">Responses Coded</div>
        </div>
        <div className="stat-box highlight">
          <div className="stat-value">{analysis.saturationScore}%</div>
          <div className="stat-label">Saturation</div>
        </div>
      </div>

      {view === 'coding' && (
        <div className="coding-view">
          <div className="code-panel">
            <h5>Codes</h5>
            <div className="add-code-form">
              <input
                type="text"
                placeholder="New code name..."
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCode()}
              />
              <button onClick={addCode}><Plus size={16} /></button>
            </div>
            <div className="code-list">
              {codes.map(code => (
                <div 
                  key={code.id} 
                  className={`code-item ${code.categoryId ? 'categorized' : ''}`}
                >
                  <span className="code-name">{code.name}</span>
                  <span className="code-count">{analysis.codeFrequency[code.id] || 0}</span>
                  <button onClick={() => removeCode(code.id)}><Trash2 size={12} /></button>
                </div>
              ))}
              {codes.length === 0 && (
                <div className="empty-state">Create codes to start coding responses</div>
              )}
            </div>
          </div>

          <div className="response-panel">
            <h5>Responses ({texts.length})</h5>
            <div className="response-list">
              {texts.map((text, idx) => (
                <div 
                  key={idx} 
                  className={`response-card ${selectedResponse === idx ? 'selected' : ''}`}
                  onClick={() => setSelectedResponse(selectedResponse === idx ? null : idx)}
                >
                  <div className="response-text">
                    {text.substring(0, 150)}{text.length > 150 ? '...' : ''}
                  </div>
                  <div className="applied-codes">
                    {(codedResponses[idx] || []).map(cId => {
                      const code = codes.find(c => c.id === cId);
                      return code ? (
                        <span key={cId} className="applied-code">{code.name}</span>
                      ) : null;
                    })}
                  </div>
                  {selectedResponse === idx && (
                    <div className="code-selector">
                      <p>Apply codes:</p>
                      <div className="code-buttons">
                        {codes.map(code => (
                          <button
                            key={code.id}
                            className={(codedResponses[idx] || []).includes(code.id) ? 'active' : ''}
                            onClick={(e) => { e.stopPropagation(); toggleCodeOnResponse(idx, code.id); }}
                          >
                            {code.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === 'axial' && (
        <div className="axial-view">
          <div className="category-panel">
            <h5>Categories</h5>
            <div className="add-category-form">
              <input
                type="text"
                placeholder="New category name..."
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCategory()}
              />
              <button onClick={addCategory}><Plus size={16} /></button>
            </div>
            
            <div className="categories-list">
              {categorizedCodes.map(cat => (
                <div key={cat.id} className="category-group">
                  <div 
                    className="category-header"
                    onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                  >
                    {expandedCategory === cat.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span className="cat-name">{cat.name}</span>
                    <span className="cat-count">{cat.codes.length} codes</span>
                    <button onClick={(e) => { e.stopPropagation(); removeCategory(cat.id); }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                  {expandedCategory === cat.id && (
                    <div className="category-codes">
                      {cat.codes.map(code => (
                        <div key={code.id} className="categorized-code">
                          <span>{code.name}</span>
                          <button onClick={() => assignCodeToCategory(code.id, null)}>
                            Remove
                          </button>
                        </div>
                      ))}
                      {cat.codes.length === 0 && (
                        <div className="empty-cat">Drag codes here</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="uncategorized-panel">
            <h5>Uncategorized Codes ({uncategorizedCodes.length})</h5>
            <div className="uncategorized-codes">
              {uncategorizedCodes.map(code => (
                <div key={code.id} className="uncategorized-code">
                  <span>{code.name}</span>
                  <select 
                    value="" 
                    onChange={e => assignCodeToCategory(code.id, e.target.value)}
                  >
                    <option value="">Assign to category...</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              ))}
              {uncategorizedCodes.length === 0 && (
                <div className="empty-state">All codes are categorized</div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'memos' && (
        <div className="memos-view">
          <div className="add-memo-form">
            <textarea
              placeholder="Write a memo about emerging patterns, insights, or theoretical ideas..."
              value={newMemo}
              onChange={e => setNewMemo(e.target.value)}
              rows={3}
            />
            <button onClick={addMemo}><Plus size={16} /> Add Memo</button>
          </div>
          <div className="memos-list">
            {memos.map(memo => (
              <div key={memo.id} className="memo-card">
                <div className="memo-date">
                  {new Date(memo.date).toLocaleDateString()}
                </div>
                <div className="memo-text">{memo.text}</div>
                <button className="delete-memo" onClick={() => removeMemo(memo.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {memos.length === 0 && (
              <div className="empty-state">
                <StickyNote size={32} />
                <p>No memos yet. Write memos to capture your theoretical insights.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'summary' && (
        <div className="summary-view">
          <div className="saturation-indicator">
            <h5>Theoretical Saturation</h5>
            <div className="saturation-bar">
              <div 
                className={`saturation-fill ${analysis.saturationLevel}`}
                style={{ width: `${analysis.saturationScore}%` }}
              />
            </div>
            <div className="saturation-labels">
              <span>Low</span>
              <span>Moderate</span>
              <span>High</span>
            </div>
            <p className="saturation-desc">
              {analysis.saturationLevel === 'high' 
                ? 'Good saturation! Most responses have been coded.'
                : analysis.saturationLevel === 'moderate'
                ? 'Continue coding to improve saturation.'
                : 'More coding needed to reach theoretical saturation.'}
            </p>
          </div>

          <div className="code-frequency">
            <h5>Code Frequency</h5>
            <div className="freq-list">
              {codes
                .sort((a, b) => (analysis.codeFrequency[b.id] || 0) - (analysis.codeFrequency[a.id] || 0))
                .map(code => (
                  <div key={code.id} className="freq-item">
                    <span className="freq-name">{code.name}</span>
                    <div className="freq-bar-container">
                      <div 
                        className="freq-bar"
                        style={{ 
                          width: `${analysis.totalResponses > 0 
                            ? (analysis.codeFrequency[code.id] / analysis.totalResponses) * 100 
                            : 0}%` 
                        }}
                      />
                    </div>
                    <span className="freq-count">{analysis.codeFrequency[code.id] || 0}</span>
                  </div>
                ))}
            </div>
          </div>

          <div className="interpretation-section">
            <h5><Info size={18} /> Analytical Summary</h5>
            <p>{groundedSummary}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroundedTheory;
